import { useState, useMemo, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import { NavIcon, SidebarToggleButton, useBrandTokens } from '../../shell/shared';
import { useAppointmentsContext } from '../../shell/AppointmentsShellLayout';
import { FiltersDrawer, type FiltersState } from './FiltersDrawer';
import { useAppointmentCalendarSettings } from './useAppointmentCalendarSettings';
import {
  getWeekdayLabels,
  startOfWeek,
  generateTimeSlots,
  minutesToHour,
  formatMinutesToTime,
} from './calendarHelpers';
import {
  useAppointmentsData,
  useAvailableSlotsData,
  appointmentToPrefill,
  slotToPrefill,
  type EnrichedAppointment,
  type EnrichedSlot,
} from './useAppointmentsData';

// ─── Constants ───

const HOUR_HEIGHT = 60; // px per hour row
const GUTTER_WIDTH = 64;

// ─── Week helpers ───

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatShortMonth(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short' });
}

function formatDateRange(monday: Date): string {
  const sunday = addDays(monday, 6);
  const mMonth = formatShortMonth(monday);
  const sMonth = formatShortMonth(sunday);
  if (mMonth === sMonth) {
    return `${mMonth} ${monday.getDate()} – ${sunday.getDate()}, ${monday.getFullYear()}`;
  }
  return `${mMonth} ${monday.getDate()} – ${sMonth} ${sunday.getDate()}, ${sunday.getFullYear()}`;
}

function formatTime(hour: number): string {
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m.toString().padStart(2, '0')} ${period}`;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Convert "HH:mm" to fractional hour (e.g. "09:30" → 9.5) */
function timeToHour(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h + (m || 0) / 60;
}

// ─── Default filters ───

const DEFAULT_FILTERS: FiltersState = {
  quickFilter: 'this-week',
  location: '',
  provider: '',
  category: '',
  status: '',
  service: '',
  room: '',
};

// ─── Week View Page ───

export function WeekViewPage() {
  const brandTokens = useBrandTokens();
  const { openBookDrawer, collapsed, onToggleSidebar } = useAppointmentsContext();
  const calSettings = useAppointmentCalendarSettings();
  const [weekStart, setWeekStart] = useState(() =>
    startOfWeek(new Date(), calSettings.weekStartsOn),
  );
  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<FiltersState>(DEFAULT_FILTERS);

  const weekdayLabels = useMemo(
    () => getWeekdayLabels(calSettings.weekStartsOn),
    [calSettings.weekStartsOn],
  );

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  // Compute date range for the week
  const startDate = useMemo(() => toDateStr(weekDays[0]), [weekDays]);
  const endDate = useMemo(() => toDateStr(weekDays[6]), [weekDays]);

  const { appointments, lookups } = useAppointmentsData(
    startDate,
    endDate,
    calSettings.colorAppointmentsBy,
  );

  // PRD.234 — Available slot overlays. Filtered by current provider/service so
  // the slot wash matches whatever the calendar is showing.
  const allSlots = useAvailableSlotsData(startDate, endDate, lookups);

  // Apply filters
  const filtered = useMemo(() => {
    return appointments.filter((a) => {
      if (search) {
        const q = search.toLowerCase();
        if (
          !a.patientName.toLowerCase().includes(q) &&
          !a.providerName.toLowerCase().includes(q) &&
          !a.serviceName.toLowerCase().includes(q)
        )
          return false;
      }
      if (filters.provider && a.providerId !== filters.provider) return false;
      if (filters.category && a.categoryId !== filters.category) return false;
      if (filters.status && a.statusId !== filters.status) return false;
      if (filters.service && a.serviceId !== filters.service) return false;
      return true;
    });
  }, [appointments, search, filters]);

  // Group by date string for day column lookup
  const appointmentsByDate = useMemo(() => {
    const map = new Map<string, EnrichedAppointment[]>();
    for (const appt of filtered) {
      if (!map.has(appt.date)) map.set(appt.date, []);
      map.get(appt.date)!.push(appt);
    }
    return map;
  }, [filtered]);

  // PRD.234 — Filter slots by the same provider/service filters as appointments.
  const slotsByDate = useMemo(() => {
    const map = new Map<string, EnrichedSlot[]>();
    for (const s of allSlots) {
      if (filters.provider && s.providerId !== filters.provider) continue;
      if (filters.service && s.serviceId !== filters.service) continue;
      if (!map.has(s.date)) map.set(s.date, []);
      map.get(s.date)!.push(s);
    }
    return map;
  }, [allSlots, filters.provider, filters.service]);

  const today = new Date();

  const handlePrevWeek = () => setWeekStart((d) => addDays(d, -7));
  const handleNextWeek = () => setWeekStart((d) => addDays(d, 7));
  const handleToday = () => setWeekStart(startOfWeek(new Date(), calSettings.weekStartsOn));

  const handleRemoveQuickFilter = () => {
    setFilters((prev) => ({ ...prev, quickFilter: '' }));
  };

  const quickFilterLabel =
    filters.quickFilter === 'today'
      ? 'Today'
      : filters.quickFilter === 'this-week'
        ? 'This Week'
        : filters.quickFilter === 'this-month'
          ? 'This Month'
          : null;

  const hoursStart = minutesToHour(calSettings.workingHoursStartMin);
  const hoursEnd = minutesToHour(calSettings.workingHoursEndMin);

  const timeSlots = useMemo(
    () =>
      generateTimeSlots(
        calSettings.workingHoursStartMin,
        calSettings.workingHoursEndMin,
        calSettings.slotIntervalMin,
      ),
    [calSettings.workingHoursStartMin, calSettings.workingHoursEndMin, calSettings.slotIntervalMin],
  );

  const gridHeight = (hoursEnd - hoursStart) * HOUR_HEIGHT;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Page Header */}
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '0 var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          {onToggleSidebar && (
            <SidebarToggleButton collapsed={collapsed} onToggle={onToggleSidebar} />
          )}
          <div>
            <h1
              style={{
                fontSize: 'var(--font-size-xl2)',
                fontWeight: 'var(--font-weight-semi)',
                lineHeight: 'var(--line-height-xl2)',
                letterSpacing: '-0.44px',
                color: 'var(--text)',
                margin: 0,
              }}
            >
              Appointments
            </h1>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                margin: '2px 0 0',
              }}
            >
              Manage clinic appointments and schedules
            </p>
          </div>
        </div>

        <Button variant="gradient" size="md" onClick={() => openBookDrawer()}>
          + Book a Patient
        </Button>
      </div>

      {/* Toolbar */}
      <div
        style={{
          padding: 'var(--sp-4) var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        {/* Week Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <button type="button" onClick={handlePrevWeek} style={navBtnStyle}>
            <NavIcon name="chevron-left" />
          </button>
          <button type="button" onClick={handleNextWeek} style={navBtnStyle}>
            <NavIcon name="chevron-right" />
          </button>
        </div>

        <span
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-1)',
          }}
        >
          {formatDateRange(weekStart)}
          <NavIcon name="chevron-down" />
        </span>

        <button type="button" onClick={handleToday} style={todayBtnStyle}>
          Today
        </button>

        {quickFilterLabel && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--sp-1)',
              height: 28,
              padding: '0 var(--sp-2) 0 var(--sp-3)',
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-primary-bg, rgba(59,130,246,0.08))',
              color: 'var(--color-primary)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              whiteSpace: 'nowrap',
            }}
          >
            {quickFilterLabel}
            <button
              type="button"
              onClick={handleRemoveQuickFilter}
              style={{
                background: 'none',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                padding: '0 2px',
                fontSize: 'var(--font-size-base)',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              ×
            </button>
          </span>
        )}

        <div style={{ flex: 1 }} />

        {/* Search */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              position: 'absolute',
              left: 12,
              color: 'var(--text-muted)',
              display: 'flex',
              pointerEvents: 'none',
            }}
          >
            <NavIcon name="search" />
          </div>
          <input
            type="text"
            placeholder="Search patient, provider, service..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: 260,
              height: 36,
              padding: '0 var(--sp-4) 0 40px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text)',
              outline: 'none',
            }}
          />
        </div>

        <button
          type="button"
          onClick={() => setFiltersOpen(true)}
          style={{
            height: 36,
            padding: '0 var(--sp-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            color: 'var(--text)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
          }}
        >
          <NavIcon name="sliders" />
          Filters
        </button>
      </div>

      {/* Week Grid */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {/* Day column headers */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `${GUTTER_WIDTH}px repeat(7, 1fr)`,
            position: 'sticky',
            top: 0,
            zIndex: 2,
            background: 'var(--bg-surface)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {/* Gutter label */}
          <div
            style={{
              padding: 'var(--sp-2) var(--sp-2)',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              borderRight: '1px solid var(--border)',
            }}
          >
            GMT+8
          </div>

          {/* Day headers */}
          {weekDays.map((day, idx) => {
            const isDayToday = isSameDay(day, today);
            return (
              <div
                key={idx}
                style={{
                  padding: 'var(--sp-3) var(--sp-2)',
                  textAlign: 'center',
                  borderRight: idx < 6 ? '1px solid var(--border)' : undefined,
                  background: isDayToday ? 'rgba(0,0,0,0.015)' : undefined,
                }}
              >
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {weekdayLabels[idx]}
                </div>
                <div
                  style={{
                    marginTop: 4,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 28,
                    padding: isDayToday ? '0 10px' : undefined,
                    borderRadius: isDayToday ? 'var(--radius-full)' : undefined,
                    background: isDayToday ? 'var(--color-primary)' : undefined,
                    color: isDayToday ? '#ffffff' : 'var(--text)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semi)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatShortMonth(day)} {day.getDate()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Time grid body */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `${GUTTER_WIDTH}px repeat(7, 1fr)`,
            position: 'relative',
          }}
        >
          {/* Time gutter */}
          <div style={{ borderRight: '1px solid var(--border)' }}>
            {timeSlots.map((slotMin) => (
              <div
                key={slotMin}
                style={{
                  height: (calSettings.slotIntervalMin / 60) * HOUR_HEIGHT,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-end',
                  padding: '0 var(--sp-2)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                  position: 'relative',
                  top: -8,
                }}
              >
                {formatMinutesToTime(slotMin)}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDays.map((day, dayIdx) => {
            const isDayToday = isSameDay(day, today);
            const dateStr = toDateStr(day);
            const dayAppts = appointmentsByDate.get(dateStr) ?? [];
            const daySlots = slotsByDate.get(dateStr) ?? [];

            return (
              <div
                key={dayIdx}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const y = e.clientY - rect.top;
                  const totalMinutes = calSettings.workingHoursStartMin + (y / HOUR_HEIGHT) * 60;
                  // Snap to slot interval
                  const snapped =
                    Math.floor(totalMinutes / calSettings.slotIntervalMin) *
                    calSettings.slotIntervalMin;
                  const h = Math.floor(snapped / 60);
                  const m = snapped % 60;
                  const startTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                  openBookDrawer({ date: dateStr, startTime });
                }}
                style={{
                  position: 'relative',
                  height: gridHeight,
                  borderRight: dayIdx < 6 ? '1px solid var(--border)' : undefined,
                  background: isDayToday ? 'rgba(0,0,0,0.015)' : undefined,
                  cursor: 'pointer',
                }}
              >
                {/* Grid lines at each slot interval */}
                {timeSlots.map((slotMin) => (
                  <div
                    key={slotMin}
                    style={{
                      position: 'absolute',
                      top: ((slotMin - calSettings.workingHoursStartMin) / 60) * HOUR_HEIGHT,
                      left: 0,
                      right: 0,
                      height: 1,
                      background: 'var(--border)',
                    }}
                  />
                ))}

                {/* PRD.234 — Available slot overlays (rendered behind appointments). */}
                {daySlots.map((s) => {
                  const startHour = timeToHour(s.startTime);
                  const top = (startHour - hoursStart) * HOUR_HEIGHT;
                  const height = (s.durationMinutes / 60) * HOUR_HEIGHT;
                  return (
                    <div
                      key={`slot-${s.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openBookDrawer(slotToPrefill(s));
                      }}
                      title={`${s.providerName || 'Provider'} · ${s.serviceName || 'Service'} · ${s.remaining} slot${s.remaining === 1 ? '' : 's'} left`}
                      style={{
                        position: 'absolute',
                        top,
                        left: 2,
                        right: 2,
                        height,
                        background:
                          'repeating-linear-gradient(135deg, rgba(59,130,246,0.06) 0 6px, rgba(59,130,246,0.12) 6px 12px)',
                        border: '1px dashed rgba(59,130,246,0.5)',
                        borderRadius: 'var(--radius-sm)',
                        padding: '2px 6px',
                        cursor: 'pointer',
                        zIndex: 0,
                        fontSize: 10,
                        color: 'rgba(30, 64, 175, 0.85)',
                        lineHeight: '14px',
                        overflow: 'hidden',
                      }}
                    >
                      {height > 24 ? `${s.remaining} open` : '·'}
                    </div>
                  );
                })}

                {/* Appointment blocks */}
                {dayAppts.map((appt) => {
                  const startHour = timeToHour(appt.startTime);
                  const durationHours = appt.durationMinutes / 60;
                  const top = (startHour - hoursStart) * HOUR_HEIGHT;
                  const height = durationHours * HOUR_HEIGHT;
                  const endHour = startHour + durationHours;

                  return (
                    <div
                      key={appt.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        openBookDrawer(appointmentToPrefill(appt));
                      }}
                      style={{
                        position: 'absolute',
                        top,
                        left: 4,
                        right: 4,
                        height,
                        background: appt.color,
                        borderRadius: 'var(--radius-sm)',
                        padding: '4px 8px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        borderLeft: '3px solid',
                        borderLeftColor: appt.accentColor,
                        zIndex: 1,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 'var(--font-weight-semi)',
                          color: '#1e293b',
                          lineHeight: '16px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {appt.patientName}
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: '#475569',
                          lineHeight: '14px',
                          marginTop: 1,
                        }}
                      >
                        {formatTime(startHour)} – {formatTime(endHour)}
                      </div>
                      {appt.serviceName && height > 40 && (
                        <div
                          style={{
                            fontSize: 10,
                            color: '#64748b',
                            lineHeight: '14px',
                            marginTop: 2,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {appt.serviceName}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters Drawer */}
      <FiltersDrawer
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        brandTokens={brandTokens}
        filters={filters}
        onApply={setFilters}
        visibleFilters={calSettings.filters}
        lookups={lookups}
      />
    </div>
  );
}

// ─── Shared styles ───

const navBtnStyle: CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: 'var(--text)',
  padding: 0,
};

const todayBtnStyle: CSSProperties = {
  height: 32,
  padding: '0 var(--sp-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  cursor: 'pointer',
};
